/**
 * WhatsApp Widget - Custom Chat System
 * Fully native Shopify implementation without third-party dependencies
 * 
 * Features:
 * - Floating button with popup chat
 * - Dynamic logo from theme settings
 * - WhatsApp redirect with prefilled message
 * - Smart visibility control (hides on overlays)
 * - Full keyboard and accessibility support
 * - Responsive design for mobile and desktop
 * - Fully configurable from Shopify Theme Customizer:
 *   * Enable/disable widget
 *   * Custom WhatsApp number
 *   * Custom greeting message
 *   * Dynamic working hours (IST timezone)
 *   * Sunday closure toggle
 *   * Holiday management (dates when closed)
 * 
 * Configuration:
 * - All settings come from window.WHATSAPP_CONFIG (set in Liquid template)
 * - Uses client-side IST timezone conversion
 * - Auto-updates status every minute
 * - Holiday-aware availability logic
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    BUTTON_ID: 'whatsapp-float-btn',
    POPUP_ID: 'whatsapp-popup',
    CLOSE_BTN_ID: 'whatsapp-close-btn',
    WIDGET_ID: 'whatsapp-widget',
    CTA_BTN_ID: 'whatsapp-cta-btn',
    HIDDEN_ATTR: 'hidden',
    ARIA_HIDDEN: 'aria-hidden',
    OVERLAY_CLASSES: [
      'drawer-open',
      'modal-open',
      'search-modal-open',
      'menu-open',
      'offcanvas-backdrop',
    ],
    OVERLAY_DATA_ATTRS: [
      'data-drawer-visible',
      'data-modal-visible',
      'data-search-visible',
    ],
  };

  // State
  let state = {
    isOpen: false,
    isInitialized: false,
  };

  // Working Hours Configuration (from Shopify settings)
  const WORKING_HOURS = {
    START: 10 * 60, // 10:00 AM in minutes (default)
    END: 17 * 60,   // 5:00 PM in minutes (default)
  };

  /**
   * Parse holidays from config
   */
  function getHolidays() {
    if (!window.WHATSAPP_CONFIG || !window.WHATSAPP_CONFIG.holidays) {
      return [];
    }
    return window.WHATSAPP_CONFIG.holidays
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
  }

  /**
   * Check if a date is a holiday
   */
  function isHoliday(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const formatted = `${yyyy}-${mm}-${dd}`;
    return getHolidays().includes(formatted);
  }

  /**
   * Get current time in IST (Asia/Kolkata timezone)
   */
  function getISTTime() {
    const now = new Date();
    const istTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
    );
    return istTime;
  }

  /**
   * Check if current time is within working hours
   */
  function isOnline() {
    if (!window.WHATSAPP_CONFIG) {
      return false;
    }

    const istTime = getISTTime();
    const day = istTime.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = istTime.getHours();
    const minute = istTime.getMinutes();
    const currentMinutes = hour * 60 + minute;

    // Get working hours from config
    const startHour = window.WHATSAPP_CONFIG.startHour || 10;
    const endHour = window.WHATSAPP_CONFIG.endHour || 17;
    const startMinutes = startHour * 60;
    const endMinutes = endHour * 60;

    // Check if Sunday and Sunday is closed
    const isSunday = day === 0;
    const sundayClosed =
      window.WHATSAPP_CONFIG.sundayClosed !== false; // Default true
    if (isSunday && sundayClosed) {
      return false;
    }

    // Check if holiday
    if (isHoliday(istTime)) {
      return false;
    }

    // Check if within working hours
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }

  /**
   * Get next available day considering holidays and Sunday closure
   */
  function getNextAvailableDay(date) {
    let next = new Date(date);
    const startHour = window.WHATSAPP_CONFIG?.startHour || 10;
    const sundayClosed =
      window.WHATSAPP_CONFIG?.sundayClosed !== false; // Default true

    while (true) {
      next.setDate(next.getDate() + 1);

      const d = next.getDay();
      const isSunday = d === 0;

      // Skip Sunday if closed
      if (isSunday && sundayClosed) continue;

      // Skip holidays
      if (isHoliday(next)) continue;

      // Found a working day
      const daysMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return { day: daysMap[d], date: next };
    }
  }

  /**
   * Update WhatsApp status based on working hours
   */
  function updateStatus() {
    const statusEl = document.querySelector('[data-status]');
    if (!statusEl) return;

    const online = isOnline();

    if (online) {
      statusEl.textContent = 'Online now';
      statusEl.setAttribute('data-status', 'online');
    } else {
      const istTime = getISTTime();
      const nextAvail = getNextAvailableDay(istTime);
      const startHour = window.WHATSAPP_CONFIG?.startHour || 10;
      const formattedHour =
        String(startHour).padStart(2, '0') + ':00';

      statusEl.innerHTML = `Offline<br><small>Next available on ${nextAvail.day} at ${formattedHour} IST</small>`;
      statusEl.setAttribute('data-status', 'offline');
    }
  }

  /**
   * Update WhatsApp CTA link with config values
   */
  function updateWhatsAppLink() {
    const ctaBtn = document.getElementById(CONFIG.CTA_BTN_ID);
    if (!ctaBtn || !window.WHATSAPP_CONFIG) return;

    const number = window.WHATSAPP_CONFIG.number;
    const message = encodeURIComponent(window.WHATSAPP_CONFIG.message);

    ctaBtn.href = `https://wa.me/${number}?text=${message}`;
  }

  /**
   * Initialize the WhatsApp widget
   */
  function init() {
    if (state.isInitialized) return;

    const widget = document.getElementById(CONFIG.WIDGET_ID);
    if (!widget) return;

    const floatBtn = document.getElementById(CONFIG.BUTTON_ID);
    const popup = document.getElementById(CONFIG.POPUP_ID);
    const closeBtn = document.getElementById(CONFIG.CLOSE_BTN_ID);
    const ctaBtn = document.getElementById(CONFIG.CTA_BTN_ID);

    if (!floatBtn || !popup || !closeBtn) return;

    // Event listeners
    floatBtn.addEventListener('click', togglePopup);
    closeBtn.addEventListener('click', closePopup);
    popup.addEventListener('click', (e) => e.stopPropagation());

    // Click outside popup to close
    document.addEventListener('click', (e) => {
      if (
        state.isOpen &&
        !widget.contains(e.target)
      ) {
        closePopup();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) {
        closePopup();
        floatBtn.focus();
      }
    });

    // Observe overlay visibility
    observeOverlayVisibility();

    // Listen for offcanvas events (Bootstrap)
    observeOffcanvasEvents();

    // Initialize and update status based on working hours
    updateStatus();
    updateWhatsAppLink();

    // Update status every minute (60000 milliseconds)
    setInterval(updateStatus, 60000);

    state.isInitialized = true;
  }

  /**
   * Toggle popup open/closed
   */
  function togglePopup() {
    if (state.isOpen) {
      closePopup();
    } else {
      openPopup();
    }
  }

  /**
   * Open popup
   */
  function openPopup() {
    if (isOverlayVisible()) {
      return; // Don't open if overlay is visible
    }

    const popup = document.getElementById(CONFIG.POPUP_ID);
    if (!popup) return;

    popup.removeAttribute(CONFIG.HIDDEN_ATTR);
    popup.setAttribute(CONFIG.ARIA_HIDDEN, 'false');
    popup.classList.add('show');
    popup.classList.remove('hide');

    state.isOpen = true;
  }

  /**
   * Close popup
   */
  function closePopup() {
    const popup = document.getElementById(CONFIG.POPUP_ID);
    if (!popup) return;

    popup.classList.remove('show');
    popup.classList.add('hide');
    popup.setAttribute(CONFIG.ARIA_HIDDEN, 'true');

    // Hide element after animation completes
    setTimeout(() => {
      popup.setAttribute(CONFIG.HIDDEN_ATTR, '');
    }, 300);

    state.isOpen = false;
  }

  /**
   * Check if any overlay is currently visible
   */
  function isOverlayVisible() {
    const body = document.body;

    // Check CSS classes
    for (const className of CONFIG.OVERLAY_CLASSES) {
      if (body.classList.contains(className)) {
        return true;
      }
    }

    // Check data attributes
    for (const attr of CONFIG.OVERLAY_DATA_ATTRS) {
      if (body.hasAttribute(attr)) {
        return true;
      }
    }

    // Check Bootstrap offcanvas backdrop
    const offcanvasBackdrop = document.querySelector('.offcanvas-backdrop.show');
    if (offcanvasBackdrop) {
      return true;
    }

    return false;
  }

  /**
   * Observe changes to body classes for overlay visibility
   */
  function observeOverlayVisibility() {
    const widget = document.getElementById(CONFIG.WIDGET_ID);
    if (!widget) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          if (isOverlayVisible()) {
            // Hide widget
            closePopup();
            widget.style.opacity = '0';
            widget.style.pointerEvents = 'none';
          } else {
            // Show widget
            widget.style.opacity = '1';
            widget.style.pointerEvents = 'auto';
          }
        }
      });
    });

    // Observe body for class changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Listen for Bootstrap offcanvas events
   */
  function observeOffcanvasEvents() {
    // For Bootstrap offcanvas
    const offcanvasElements = document.querySelectorAll('.offcanvas');

    offcanvasElements.forEach((offcanvas) => {
      offcanvas.addEventListener('show.bs.offcanvas', () => {
        closePopup();
      });

      offcanvas.addEventListener('hidden.bs.offcanvas', () => {
        // Re-check visibility after offcanvas closes
        setTimeout(() => {
          if (!isOverlayVisible()) {
            const widget = document.getElementById(CONFIG.WIDGET_ID);
            if (widget) {
              widget.style.opacity = '1';
              widget.style.pointerEvents = 'auto';
            }
          }
        }, 100);
      });
    });
  }

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API for external use
  window.whatsappWidget = {
    open: openPopup,
    close: closePopup,
    toggle: togglePopup,
    isOpen: () => state.isOpen,
  };
})();
